import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import SalonService from "../../services/SalonService";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css"; // Import default styles
import "./CalendarStyles.css"; // Import custom styles
import moment from "moment";
import "./AppointmentBooking.css";
import "../../../src/style.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faGlobe, faPhone } from "@fortawesome/free-solid-svg-icons";
import LoadingIndicator from '../loadingIndicator/LoadingIndicator';
import ShowSalonImages from "../salonImages/ShowSalonImages";
import { useSalon } from "../../context/SalonContext";
import { toast } from "react-toastify";

const AppointmentBooking = () => {
    const { salonUuid, customerId } = useSalon();
    const navigate = useNavigate();
    const currentDate = new Date();
    const [data, setData] = useState(null);
    const [salonImages, setSalonImages] = useState(null);
    const today = moment().format('YYYY-MM-DD');
    const oneMonthLater = moment().add(1, "months").format("YYYY-MM-DD");
    const [appointmentDate, setAppointmentDate] = useState(today);
    const [loading, setLoading] = useState(true);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [servicesData, setServicesData] = useState([]);
    const [isTodayHoliday, setIsTodayHoliday] = useState(false);
    const [salonEmps, setSalonEmps] = useState([]);
    const [slots, setSlots] = useState([]);
    const [bkgAvlMsg, setBkgAvlMsg] = useState(null);
    const [bookApntDisable, setBookApntDisable] = useState(false);
    const [disabledDates, setDisabledDates] = useState([]);
    const [customerMobileNumber, setCustomerMobileNumber] = useState("");
    const [serviceIds] = useState([]);
    const [empId, setEmpId] = useState(null);
    const [empName, setEmpName] = useState(null);

    const [advanceSummary, setAdvanceSummary] = useState(null);
    const [showAllEmps, setShowAllEmps] = useState(false);
    const [showDatewiseEmps, setShowDatewiseEmps] = useState(false);
    const [showSlotwiseEmps, setShowSlotwiseEmps] = useState(false);
    const [shopOpensAt, setShopOpensAt] = useState(null);
    const [shopClosesAt, setShopClosesAt] = useState(null);
    const [breakTimings, setBreakTimings] = useState(null);
    let timeInMinutes = (currentDate.getHours() * 60) + currentDate.getMinutes();
    const timeSlotMinutes = 60;
    const [serviceAtHome, setServiceAtHome] = useState(false);
    const pressTimer = useRef(null);
    const [showFullAbout, setShowFullAbout] = useState(false);
    const MAX_ABOUT_LENGTH = 120; // chars before collapsing

    const timeout = setTimeout(() => { setLoading(false); }, 30000);

    const selectedServices = servicesData.filter(s => s.checked);

    const serviceTotal = selectedServices.reduce(
        (sum, s) => sum + (s.servicePrice || 0),
        0
    );

    useEffect(() => {
        const isLoggedIn = localStorage.getItem("ACCESS_TOKEN");
        if (!isLoggedIn) {
            navigate(`/login?redirect=/booking`);
        }

        let mySalonUuid = salonUuid ? salonUuid : localStorage.getItem("SALON_UUID");
        if (!mySalonUuid) {
            navigate("/home", { state: { message: "Salon details not found. Please try again or contact salon." } });
        } else {
            getSalonDetails(mySalonUuid);
            getSalonImages(mySalonUuid);
            getSalonServices(mySalonUuid);
            getSalonEmps('datewise', today, null, mySalonUuid);
            setCustomerMobileNumber(localStorage.getItem("MOBILE_NUMBER"));
        }
        return () => clearTimeout(timeout); // Cleanup on unmount
    }, [navigate, today]);

    useEffect(() => {
        // ✅ Required data check
        if (
            !salonUuid ||
            !appointmentDate ||
            !selectedSlot ||
            serviceTotal <= 0
        ) {
            setAdvanceSummary(null);
            return;
        }

        // ✅ Call advance summary
        getAdvanceSummary(salonUuid, serviceTotal);

    }, [
        salonUuid,
        appointmentDate,
        selectedSlot?.apntStart, // important: stable dependency
        serviceTotal
    ]);

    const showReviewLinks = (reviewsLink) => {
        const reviewLinks = reviewsLink
            ? reviewsLink.split(",").map(link => link.trim())
            : [];

        const getReviewLabel = (url) => {
            if (url.includes("g.page") || url.includes("maps.google")) {
                return "Google Reviews";
            } else if (url.includes("facebook.com")) {
                return "Facebook Reviews";
            }
            return "Other Reviews";
        };

        return (
            reviewLinks.length > 0 && (
                <div className="div-row-style">
                    <span className="label-style">Reviews: </span>
                    {reviewLinks.map((link, index) => (
                        <a
                            key={index}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="review-link"
                        >
                            {getReviewLabel(link)}
                            {index < reviewLinks.length - 1 ? ", " : ""}
                        </a>
                    ))}
                </div>
            )
        );
    };

    async function getTimeSlots(date) {
        // Reset state
        setBkgAvlMsg(null);
        setSelectedSlot(null);

        const formattedDate = moment(date).format('YYYY-MM-DD');
        setAppointmentDate(formattedDate);
        setLoading(true);

        const mySalonUuid = salonUuid || localStorage.getItem('SALON_UUID');
        if (!mySalonUuid) {
            toast.error('Salon ID not found');
            setLoading(false);
            return;
        }

        try {
            const response = await SalonService.getSalonDataById(mySalonUuid, formattedDate);
            clearTimeout?.(timeout); // guard in case timeout is undefined
            const data = response?.data;
            setData(data);
            setSlots(data?.slots || []);
            prepareOtherData(data, date);
        } catch (err) {
            clearTimeout?.(timeout);
            //console.error('Error fetching salon data:', err);
            toast.error('Unable to load time slots. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    async function getSalonEmps(from, date, slot, salonUuid) {
        setLoading(true);
        setSalonEmps([]);

        const selectedDate = slot?.apntDate || date || null;
        const slotTime = slot?.apntStart || null;
        const mySalonUuid = salonUuid || localStorage.getItem('SALON_UUID');

        if (!mySalonUuid) {
            toast.error('Salon ID not found');
            setLoading(false);
            return;
        }

        try {
            const response = await SalonService.fetchSalonEmps(mySalonUuid, selectedDate, slotTime);

            setSalonEmps(response?.data || []);

            // Handle which set of employees to show
            setEmpView(from);
            clearTimeout?.(timeout);
        } catch (err) {
            //console.error('Error fetching salon employees:', err);
            setEmpView('none'); // fallback: hide all
            clearTimeout?.(timeout);
            toast.error('Unable to load salon employees. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    // 🔹 Helper: centralize employee view toggles
    function setEmpView(mode) {
        switch (mode) {
            case 'datewise':
                setShowDatewiseEmps(true);
                setShowSlotwiseEmps(false);
                setShowAllEmps(false);
                break;
            case 'slotwise':
                setShowDatewiseEmps(false);
                setShowSlotwiseEmps(true);
                setShowAllEmps(false);
                break;
            case 'allEmps':
                setShowDatewiseEmps(false);
                setShowSlotwiseEmps(false);
                setShowAllEmps(true);
                break;
            default:
                setShowDatewiseEmps(false);
                setShowSlotwiseEmps(false);
                setShowAllEmps(false);
        }
    }


    async function getSalonServices(salonUuid) {
        if (!salonUuid) {
            toast.error('Salon ID is missing');
            return;
        }

        setLoading(true);

        try {
            const response = await SalonService.getSalonServices(salonUuid);
            clearTimeout?.(timeout);                  // guard in case timeout is undefined
            setServicesData(response?.data || []);    // fallback to empty array if no data
        } catch (err) {
            clearTimeout?.(timeout);
            //console.error('Error fetching salon services:', err);
            toast.error('Unable to load salon services. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    async function getAdvanceSummary(salonUuid, serviceTotal) {
        if (!salonUuid || serviceTotal <= 0) return;

        try {
            const response = await SalonService.getAdvanceSummary({
                salonUuid,
                serviceTotal
            });
            setAdvanceSummary(response?.data);
        } catch (err) {
            //console.error("Error fetching advance summary:", err);
            toast.error("Unable to calculate advance amount");
        }
    }
    async function getSalonDetails(salonUuid) {
        if (!salonUuid) {
            toast.error('Salon ID is missing');
            return;
        }

        setLoading(true);

        try {
            const response = await SalonService.getSalonDataById(salonUuid, appointmentDate);
            clearTimeout?.(timeout); // safe even if timeout is undefined

            const data = response?.data;
            if (!data) {
                throw new Error('No salon data received');
            }

            setData(data);
            setSlots(data.slots || []);
            prepareShopClosingDays(data.shopClosingDays || []);
            prepareOtherData(data, appointmentDate);
            prepareDisabledDates(data.shopClosingDays || []);

        } catch (err) {
            clearTimeout?.(timeout);
            //console.error('Error fetching salon details:', err);
            toast.error('Unable to load salon details. Please try again.');
        } finally {
            setLoading(false);
        }
    }


    function prepareShopClosingDays(scDays) {
        let markedDates2 = {};
        scDays.forEach(date => {
            markedDates2[date] = {
                disabled: true,
                marked: true,
                disableTouchEvent: true,
                selected: true,
                selectedColor: 'grey',
                activeOpacity: 0
            };
        });
    }

    function getSalonImages(salonUuid) {
        SalonService.getSalonImages(salonUuid).then((response) => {
            clearTimeout(timeout);
            setSalonImages(response.data);
        }).catch(() => {
            clearTimeout(timeout);
        }).finally(() => setLoading(false));
    }

    function prepareDisabledDates(scDays) {
        setDisabledDates(scDays.map(date => moment(date).format("YYYY-MM-DD")))
    }

    function prepareOtherData(data, apntDate) {
        let formatedApntDate = moment(apntDate).format('YYYY-MM-DD');
        setShopOpensAt(data.shopOpensAt);
        setShopClosesAt(data.shopClosesAt);
        setBreakTimings(data.breakTimings);
        let isHoliday = data.shopClosingDays.includes(formatedApntDate);
        setIsTodayHoliday(isHoliday);
        let isFutureDate = formatedApntDate > today;

        if (isHoliday || (!isFutureDate && (timeInMinutes >= (data.closingTime - timeSlotMinutes)))) {
            setBookApntDisable(true);
        } else {
            setBookApntDisable(false);
        }
    }

    function selectSlot(slotItem) {
        setSelectedSlot(slotItem);
        setBkgAvlMsg(slotItem.empCount > slotItem.bookedCount ? `${slotItem.empCount - slotItem.bookedCount} bookings available` : null);
    }

    async function bookAppointment() {
        // 1️⃣  Validate form first
        if (!validation()) return;

        const mobile = customerMobileNumber?.trim();
        if (!mobile || mobile.length < 13) {
            toast.error('Mobile number missing or invalid. Please update your mobile number.');
            navigate('/login', { replace: true });
            return;
        }

        if (!selectedSlot || !serviceIds?.length) return;

        // 2️⃣  Home service time constraint
        const needsFourHourGap =
            serviceAtHome &&
            appointmentDate === today &&
            selectedSlot.apntStart < timeInMinutes + 240;

        if (needsFourHourGap) {
            toast.info('Service at home requires at least 4 hours lead time.');
            return;
        }

        // 3️⃣  Prepare appointment payload
        const mySalonUuid = salonUuid || localStorage.getItem('SALON_UUID');
        if (!mySalonUuid) {
            toast.error('Salon ID not found.');
            return;
        }

        const appointmentData = {
            salonUuid: mySalonUuid,
            bookingDate: appointmentDate,
            apntStart: selectedSlot.apntStart,
            apntEnd: selectedSlot.apntEnd,
            serviceIds,
            mobileNumber: mobile,
            slot: selectedSlot.slotTime,
            deviceId: null,
            timeZone: 'Asia/Kolkata',
            empId,
            empName,
            advanceRequired: advanceSummary.advanceRequired,
            advanceAmount: advanceSummary.advanceRequired ? advanceSummary.finalPayable : 0,
            paymentStatus: advanceSummary.advanceRequired ? "PENDING" : "NOT_REQUIRED",
            totalAmount: serviceTotal,
        };
        // 4️⃣  Make API call
        try {
            setLoading(true);
            const res = await SalonService.bookAppointment(appointmentData);
            const data = res?.data;
            if (!res.data?.bookingRef) return;

            if (advanceSummary?.advanceRequired) {
                navigate(`/pay/${res.data.bookingRef}`, {
                    state: {
                        customerId: customerId,
                        salonUuid: salonUuid,
                        bookingRef: res.data.bookingRef,
                        amount: advanceSummary.finalPayable,
                    },
                })

            } else {
                // toast.success("Appointment booked successfully");
                // navigate("/home");
                toast.success('Appointment booked successfully');
                navigate('/home', { replace: true });
                return; // stop execution after navigation
            }

            // if (data?.success) {
            //     toast.success('Appointment booked successfully');
            //     navigate('/home', { replace: true });
            //     return; // stop execution after navigation
            // }

            toast.warn(data?.message || 'Booking failed. Please try again.');
        } catch (err) {
            clearTimeout?.(timeout); // guard in case timeout is undefined
            const msg = err?.response?.data?.message || err?.message || 'An unexpected error occurred';
            //console.error('Booking error:', err);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }

    function validation() {
        if (!selectedSlot) {
            toast.error('Please select a booking slot');
            return false;
        }

        if (!Array.isArray(serviceIds) || serviceIds.length === 0) {
            toast.error('Please select at least one service');
            return false;
        }

        if (!appointmentDate) {
            toast.error('Please select a booking date');
            return false;
        }

        return true;
    }


    function showSalonDetails() {
        if (!data) return null;

        return (
            <div className="salon-details">
                <div className="salon-info">
                    {/* 📍 Address */}
                    {data.address && (
                        <div className="info-row">
                            <FontAwesomeIcon icon={faHome} size="lg" color="#033594" />
                            <span className="info-text">{data.address}</span>
                        </div>
                    )}

                    {/* 📝 About */}
                    {data.comments && (
                        <div className="info-section">
                            <h4 className="section-title">About</h4>
                            <p className="info-text">
                                {showFullAbout || data.comments.length <= MAX_ABOUT_LENGTH
                                    ? data.comments
                                    : `${data.comments.slice(0, MAX_ABOUT_LENGTH)}...`}
                            </p>
                            {data.comments.length > MAX_ABOUT_LENGTH && (
                                <button
                                    className="read-more-btn"
                                    onClick={() => setShowFullAbout(!showFullAbout)}
                                >
                                    {showFullAbout ? "Read less ▲" : "Read more ▼"}
                                </button>
                            )}
                        </div>
                    )}

                    {/* 🌐 Website */}
                    {data.webURL && (
                        <div className="info-row">
                            <FontAwesomeIcon icon={faGlobe} size="lg" color="#033594" />
                            <a
                                href={data.webURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="info-link"
                            >
                                {data.webURL}
                            </a>
                        </div>
                    )}

                    {/* ⭐ Reviews */}
                    {data.reviewsLink && (
                        <div className="info-row">
                            {showReviewLinks(data.reviewsLink)}
                        </div>
                    )}

                    {/* 📞 Phone */}
                    {data.mobileNumber && (
                        <div className="info-row">
                            <FontAwesomeIcon icon={faPhone} size="lg" color="#033594" />
                            <a href={`tel:${data.mobileNumber}`} className="info-link">
                                {data.mobileNumber}
                            </a>
                        </div>
                    )}

                    {/* 🕒 Hours / Holiday Notice */}
                    {isTodayHoliday ? (
                        <div className="holiday-msg">
                            🚫 Today is a holiday, please select another day.
                        </div>
                    ) : (
                        <div className="shop-hours">
                            <div className="info-row">
                                <span className="label">Shop Hours:</span>
                                <span className="info-text">
                                    {shopOpensAt} - {shopClosesAt}
                                </span>
                            </div>
                            {breakTimings && (
                                <div className="info-row">
                                    <span className="label">Break:</span>
                                    <span className="info-text">{breakTimings}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 💰 Discount */}
                    {data.discountMessage && (
                        <div className="discount-banner">
                            🎉 {data.discountMessage}
                        </div>
                    )}
                </div>

                {/* 👩‍💼 Staff link */}
                {salonEmps?.length > 0 && showAllSalonEmps()}
            </div>
        );
    }

    function showAllSalonEmps() {
        return (
            <div className="text-style">
                <a className="show-employees-link"
                    onClick={(e) => { e.preventDefault(); getSalonEmps("allEmps", null, null, salonUuid); }}>Show Salon Staff</a>
                {allEmpsModal()}
            </div>
        );
    }

    function allEmpsModal() {
        if (!showAllEmps) return null; // Don't render if modal is not visible

        return (
            <div className="modal-overlay">
                {loading && <LoadingIndicator />}
                {/* <div className="modal-div"> */}
                <div className="modal-content">
                    <div className="label-style">{"Salon Staff"}</div>

                    {salonEmps.length === 0 ? (<div className="error-message">{"Staff not configured"}</div>) : (
                        <div className="employee-list">
                            {salonEmps.map((emp, index) => renderEmp({ item: emp, index }))}
                        </div>
                    )}
                    <button className="close-button" onClick={() => setShowAllEmps(false)}>{"Close"}</button>
                </div>
                {/* </div> */}
            </div>
        );
    }

    const renderEmp = ({ index, item }) => {
        return (
            <div key={"vi_" + index} className="employee-card">
                <img src={`data:image/jpeg;base64,${item.image}`} alt={item.name} className="employee-image" />
                <div className="employee-details">
                    <div className="text-style">{item.name}, {item.gender === "M" ? "Male" : "Female"}, {item.expYears} years exp</div>
                    <div className="text-style">{item.title}</div>
                </div>
            </div>
        );
    };

    function selectAndClose() {
        if (empId == null) {
            toast.error('Please select the beautician you are looking for');
        } else {
            setShowSlotwiseEmps(false);
        }
    }

    function cancel() {
        setEmpId(null);
        setEmpName(null);
        setShowSlotwiseEmps(false);
    }

    function slotEmpsModal() {
        if (!showSlotwiseEmps) return null;

        return (
            <div className="modal-overlay">
                {loading && <LoadingIndicator />}
                {/* <div className="modal-div"> */}
                <div className="modal-content">
                    <div className="label-style">Available staff for this time slot</div>

                    {salonEmps.length === 0 ? (
                        <div className="error-message">Staff not configured</div>
                    ) : (
                        <div className="employee-list">
                            {salonEmps.map((emp, index) => renderSlotEmp({ item: emp, index }))}
                        </div>
                    )}

                    {/* ✅ Buttons fixed at bottom */}
                    <div className="slot-close-buttons-div">
                        {salonEmps.length > 0 && (
                            <button className="slot-close-button" onClick={() => selectAndClose()}>
                                Select & Close
                            </button>
                        )}
                        <button className="slot-close-button" onClick={() => cancel()}>
                            Cancel
                        </button>
                    </div>
                </div>
                {/* </div> */}
            </div>
        );
    }

    const renderSlotEmp = ({ index, item }) => {
        const isSelected = empId === item.empId;

        return (
            <div
                key={"emp_" + index}
                className={`employee-card ${isSelected ? "selected" : ""}`}
                onClick={() => {
                    setEmpId(item.empId);
                    setEmpName(item.name);
                }}
            >
                <img
                    src={`data:image/jpeg;base64,${item.image}`}
                    alt={item.name}
                    className="employee-image"
                />
                <div className="employee-title">{item.name}</div>
                <div className="employee-role">
                    {item.gender === "M" ? "Male" : "Female"}, {item.expYears} yrs exp
                </div>
                <div className="employee-role">{item.title}</div>

                {/* hidden radio for accessibility */}
                <input
                    type="radio"
                    name="employee"
                    value={item.empId}
                    checked={isSelected}
                    onChange={() => {
                        setEmpId(item.empId);
                        setEmpName(item.name);
                    }}
                    className="employee-radio"
                />
            </div>
        );
    };


    function showSalonServices(servicesData, prepareServices) {
        if (!servicesData) {
            return null;
        }

        return (
            <div className="service-container">
                <div className="service-title">{"Choose the services"}</div>

                <div className="table-wrapper"> {/* Add wrapper for scroll effect */}
                    <table className="service-table">
                        <thead>
                            <tr>
                                <th className="header-select label-style">Select</th>
                                <th className="label-style">Service Name</th>
                                <th className="label-style">Service Time (Minutes)</th>
                                <th className="header-service-price label-style">Service Price (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {servicesData.map((item, index) => renderService({ index, item, prepareServices }))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    const renderService = ({ index, item, prepareServices }) => {
        return (
            <tr key={"vi_" + item.id}>
                <td className="text-style">
                    <input
                        type="checkbox"
                        checked={item.checked}
                        onChange={() => prepareServices(index, item)}
                    />
                </td>
                <td className="text-style">{item.description}</td>
                <td className="text-style">{item.serviceTime} min</td>
                <td className="text-style">₹{item.servicePrice}</td>
            </tr>
        );
    };


    function prepareServices(index, row) {
        var list = Object.assign([], servicesData);
        let isChecked = !list[index].checked;
        list[index].checked = isChecked
        setServicesData(list);
        if (isChecked) {
            if (row.name === 'HOME_SERVICE') {
                setServiceAtHome(true);
            }
            serviceIds.push(row.id);
        } else {
            if (row.name === 'HOME_SERVICE') {
                setServiceAtHome(false);
            }
            const index = serviceIds.indexOf(row.id);
            if (index > -1) {
                serviceIds.splice(index, 1); // 2nd parameter means remove one item only
            }
        }
    }

    const datewiseEmployees = () => {
        if (!showDatewiseEmps || salonEmps.length === 0)
            return null;
        return (
            <div className="employee-container">
                <div className="service-title">
                    {'Available staff on'} {moment(appointmentDate).format("DD-MM-YYYY")}
                </div>

                <div className="employee-scroll">
                    {salonEmps.map((emp, index) => (
                        <div key={index} className="employee-item">{renderEmp({ item: emp })}</div>
                    ))}
                </div>
            </div>
        );
    };

    const showCalendarData = () => {
        return (
            <div className="calendar-container booking-card">
                <div className="service-title">Select Appointment Date</div>
                <Calendar
                    onChange={(date) => {
                        getTimeSlots(date);
                        getSalonEmps('datewise', moment(date).format('YYYY-MM-DD'), null, salonUuid);
                    }}
                    value={new Date(appointmentDate)}
                    tileDisabled={({ date }) => {
                        const formattedDate = moment(date).format("YYYY-MM-DD"); // Convert date to string
                        return (formattedDate < today || formattedDate > oneMonthLater || disabledDates.includes(formattedDate));
                    }}
                />
            </div>
        )
    }

    const showSlotsData = () => {
        if (isTodayHoliday) return null;

        return (
            <div className="slots-container booking-card">
                <div className="service-title">Select Time Slot</div>

                <div className="slot-buttons">
                    {slots.map((slot, index) => {
                        const isSelected = selectedSlot?.apntStart === slot.apntStart;

                        return (
                            <button
                                key={index}
                                className={`slot-button ${isSelected ? "selected" : ""}`}
                                onClick={() => selectSlot(slot)}
                                onMouseDown={() => handlePressStart(slot)}  // Desktop long-press
                                onMouseUp={handlePressEnd}
                                onMouseLeave={handlePressEnd}
                                onTouchStart={() => handlePressStart(slot)} // Mobile long-press
                                onTouchEnd={handlePressEnd}
                                disabled={!slot.slotAvailable}
                            >
                                {slot.slotTime}
                            </button>
                        );
                    })}
                </div>

                {bkgAvlMsg && (
                    <div className="slot-selection-msg-style">
                        <span>🕑 {bkgAvlMsg}</span>
                    </div>
                )}

                {slotEmpsModal()}
            </div>
        );
    };

    const showSummary = () => {
        return (
            <div className="selected-services booking-card">
                <h4>Booking Summary</h4>

                {/* Selected Date */}
                {appointmentDate && (
                    <div className="summary-row">
                        <span className="label">Date:</span>
                        <span className="value">{moment(appointmentDate).format("DD/MM/YYYY")}</span>
                    </div>
                )}

                {/* Selected Time */}
                {selectedSlot && (
                    <div className="summary-row">
                        <span className="label">Time:</span>
                        <span className="value">{selectedSlot.slotTime}</span>
                    </div>
                )}

                {/* Selected Staff */}
                {empName && (
                    <div className="summary-row">
                        <span className="label">Staff:</span>
                        <span className="value">{empName} ✅</span>
                    </div>
                )}

                {/* Services */}
                <ul className="services-list">
                    {servicesData
                        .filter(s => s.checked)
                        .map(s => (
                            <li key={s.id}>
                                <span>{s.description}</span>
                                <span>
                                    {s.serviceTime ? `${s.serviceTime} min` : ""}
                                    {s.serviceTime && s.servicePrice ? " | " : ""}
                                    {s.servicePrice ? `₹${s.servicePrice}` : ""}
                                </span>
                            </li>
                        ))}
                </ul>

                {/* Totals */}
                {(servicesData.some(s => s.checked && s.serviceTime) ||
                    servicesData.some(s => s.checked && s.servicePrice)) && (
                        <div className="totals">
                            {servicesData.some(s => s.checked && s.serviceTime) && (
                                <div className="totals-row">
                                    <span>Total Time:</span>
                                    <span>
                                        {servicesData
                                            .filter(s => s.checked && s.serviceTime)
                                            .reduce((sum, s) => sum + s.serviceTime, 0)}{" "}
                                        min
                                    </span>
                                </div>
                            )}
                            {servicesData.some(s => s.checked && s.servicePrice) && (
                                <div className="totals-row">
                                    <span>Total Price:</span>
                                    <span>
                                        ₹
                                        {servicesData
                                            .filter(s => s.checked && s.servicePrice)
                                            .reduce((sum, s) => sum + s.servicePrice, 0)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
            </div>
        );
    };

    const paymentSummary = () => {
        return (
            <div className="advance-card">
                <div className="advance-title">
                    Advance Payment Required
                </div>

                <div className="advance-row">
                    <span className="label">Service Total</span>
                    <span className="value">₹{advanceSummary.totalAmount}</span>
                </div>

                {advanceSummary.discount > 0 && (
                    <div className="advance-row discount">
                        <span className="label">Discount</span>
                        <span className="value">-₹{advanceSummary.discount}</span>
                    </div>
                )}

                <div className="advance-row payable">
                    <span className="label">Advance Payable</span>
                    <span className="value">₹{advanceSummary.finalPayable}</span>
                </div>

                <div className="advance-note">
                    {advanceSummary.refundPolicyText}
                </div>

                {advanceSummary.disclaimer && (
                    <div className="advance-disclaimer">
                        {advanceSummary.disclaimer}
                    </div>
                )}
            </div>
        );
    }

    // Start timer on press
    const handlePressStart = (slot) => {
        pressTimer.current = setTimeout(() => {
            setShowSlotwiseEmps(true);
            getSalonEmps('slotwise', null, slot, salonUuid);
        }, 500); // Long press duration (500ms)
    };

    // Clear timer if released early
    const handlePressEnd = () => {
        clearTimeout(pressTimer.current);
    };

    return (
        <div className="app-container">
            <div className="app-sub-container">
                {data != null && <div className="header-title"><strong>{data.salonName}</strong></div>}

                {salonImages?.length > 0 && <ShowSalonImages salonImages={salonImages} />}

                {showSalonDetails()}

                {datewiseEmployees()}

                {showSalonServices(servicesData, prepareServices)}

                {showCalendarData()}

                {showSlotsData()}

                {loading && <LoadingIndicator />}

                {servicesData.some(s => s.checked) && showSummary()}

                {advanceSummary?.advanceRequired && paymentSummary()}

                <button
                    className="book-btn"
                    disabled={bookApntDisable}
                    onClick={bookAppointment}
                >
                    {advanceSummary?.advanceRequired
                        ? "Pay & Confirm Booking"
                        : "Book Appointment"}
                </button>

            </div>
        </div>
    );
};

export default AppointmentBooking;